import { IonButton, IonIcon } from '@ionic/react';
import {
  checkmarkCircleOutline,
  alertCircleOutline,
  closeCircleOutline,
  folderOpenOutline,
} from 'ionicons/icons';
import style from './folder-flattener.module.scss';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

type FlattenProps = {
  response: any;
  onFinish: () => void;
};

export default function Flatten({ response, onFinish }: FlattenProps) {
  const theme = useSelector((state: RootState) => state.themeState.theme);
  const {
    state,
    message,
    totalProcessedFiles,
    totalFilesRequested,
    totalProcessedSize,
    failedFiles,
  } = response;

  const columnDefs = [
    { headerName: 'File Path', field: 'current_path', flex: 1 },
    { headerName: 'Error', field: 'error', flex: 1 },
  ];

  const stateConfig = {
    success: {
      icon: checkmarkCircleOutline,
      bannerClass: style['result-banner--success'],
      label: 'Complete',
    },
    partial_success: {
      icon: alertCircleOutline,
      bannerClass: style['result-banner--partial'],
      label: 'Partially Complete',
    },
    failed: {
      icon: closeCircleOutline,
      bannerClass: style['result-banner--failed'],
      label: 'Failed',
    },
  }[state as 'success' | 'partial_success' | 'failed'] ?? {
    icon: alertCircleOutline,
    bannerClass: style['result-banner--partial'],
    label: 'Unknown',
  };

  return (
    <div className={style['flatten-result']}>
      <div className={`${style['result-banner']} ${stateConfig.bannerClass}`}>
        <IonIcon icon={stateConfig.icon} className={style['result-icon']} />
        <div>
          <h2 className={style['result-title']}>{stateConfig.label}</h2>
          <p className={style['result-message']}>{message}</p>
        </div>
      </div>

      <div className={style['result-stats']}>
        <div className={style['stat-card']}>
          <IonIcon icon={folderOpenOutline} />
          <span className={style['stat-value']}>
            {totalProcessedFiles?.toLocaleString() ?? 0}
          </span>
          <span className={style['stat-label']}>Files Processed</span>
        </div>

        {totalFilesRequested != null && (
          <div className={style['stat-card']}>
            <span className={style['stat-value']}>
              {totalFilesRequested.toLocaleString()}
            </span>
            <span className={style['stat-label']}>Total Requested</span>
          </div>
        )}

        {totalProcessedSize && (
          <div className={style['stat-card']}>
            <span className={style['stat-value']}>{totalProcessedSize}</span>
            <span className={style['stat-label']}>Data Processed</span>
          </div>
        )}

        {failedFiles?.length > 0 && (
          <div className={`${style['stat-card']} ${style['stat-card--warn']}`}>
            <IonIcon icon={alertCircleOutline} />
            <span className={style['stat-value']}>{failedFiles.length}</span>
            <span className={style['stat-label']}>Sent to Review</span>
          </div>
        )}
      </div>

      {failedFiles && failedFiles.length > 0 && (
        <div className={style['result-failed-section']}>
          <h3>Files Sent to Review Folder</h3>
          <p className={style['result-failed-description']}>
            These files had name conflicts and were copied to the{' '}
            <strong>Review/</strong> subfolder with a timestamp prefix. You can
            inspect them there.
          </p>
          <div
            className={`ag-theme-alpine${theme === 'dark' ? '-dark' : ''} ${style['flatten-grid']}`}
          >
            <AgGridReact
              className={`ag-theme-alpine${theme === 'dark' ? '-dark' : ''} ${style['grid']}`}
              rowData={failedFiles}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true }}
              rowSelection="single"
              suppressScrollOnNewData={true}
              animateRows={false}
              rowBuffer={10}
            />
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '30px',
          paddingBottom: '20px',
        }}
      >
        <IonButton
          onClick={onFinish}
          style={{
            '--border-radius': '6px',
            width: '200px',
            fontWeight: 'bold',
          }}
        >
          START NEW
        </IonButton>
      </div>
    </div>
  );
}
